import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getPopupRegistration, openPopup, closePopup, Priority } from 'app/PopupHolder';
import SimpleModal from 'components/base/SimpleModal';
import SVG from 'components/base/SVG';
import InfoAlert from 'app/components/InfoAlert';
import noop from 'utils/noop';
import {
  loadContactNotes,
  saveContactNote,
  loadContactDocuments,
  uploadContactDocument,
  deleteContactDocument,
  resetContactExtras,
  sameContactId,
  selectContactExtrasContactId,
  selectContactNote,
  selectContactNoteLoaded,
  selectContactNoteSaving,
  selectContactDocuments,
  selectContactDocumentUploading,
  selectContactDocumentDeleting,
} from 'data/contacts';
// Same file, same contactUrl helper Notes/Documents use above (data/contacts/
// psCoreService.js) -- not the investor-id-scoped data/investors/
// investorProfileService.js, since these four are keyed by the contact's own
// id, same as Notes/Documents.
import {
  fetchInvestorStatuses,
  fetchTouchpoints,
  putInvestorStatus,
  putTouchpoint,
} from 'data/contacts/psCoreService';
import { fetchInvestorSavedGroups } from 'data/investors/investorProfileService';
import { setInvestorContacts } from 'data/investors/actions';
import { selectIsClickToDialOpen } from 'data/user';

import Dropdown from '../SearchFilter/Dropdown';
import CommonNotes from '../../Campaigns/Dialer/ManageNotes';
import useInvestorProfileData from '../useInvestorProfileData';
import IdentityHeader from './IdentityHeader';
import ContactInfo from './ContactInfo';
import MetricGrid from './MetricGrid';
import History from './History';
import PropertyTable from './PropertyTable';
import {
  InvestorStatusOptions,
  TouchpointOptions,
  DefaultInvestorStatus,
  DefaultTouchpoint,
} from './constants';
import { formatContactSourceDate } from './format';
import css from './style.scss';

// {id, name, seq, selected} (investor-service, numeric ids) -> Dropdown's
// {value, label}.
const toDropdownOptions = (rows) => (rows || []).map((r) => ({ value: r.id, label: r.name }));

// The row this contact currently has saved, if any -- `selected` is only
// populated when the GET was called with `?contactId=`.
const selectedValue = (rows) => (rows || []).find((r) => r.selected)?.id;

// The Investor Profile UI (ATAI-111 / S5) — Figma "contact cards", Property
// 1=Investor Profile (383:16287): one component used both as a modal over
// Search ("View Details" on an investor card) and, later, as a full page from
// the Contacts Investor List. Opened with the investor object the Search
// results already hold in memory, so the modal never shows a blank shell —
// useInvestorProfileData then materializes it (ATAI-113 — see its own header
// comment for why that save stays, ATAI-182) and fetches the real
// ATAI-109 profile snapshot and ATAI-169 owned/sold properties by id in the
// background, and `investor` below is the two merged (real data winning
// once it resolves), so every child keeps reading a plain `investor` prop
// same as before. Opened by id from the Contacts Investor List too (see
// Contacts/InvestorProfileRoute.js), via the same `.open({ investor })` call
// as Search's "View Details" — that path only ever has a thin `{id, saved}`
// object, which is why useInvestorProfileData guards against re-saving a
// thin payload over a richer snapshot (see its own header comment).
//
// S8 (ATAI-101) is now Dev Completed — Notes / Files / Investor Status /
// Touchpoint all persist for real, via investor-service, and ALL FOUR now
// go through the same file: data/contacts/psCoreService.js's contactUrl
// helper (Notes/Documents already lived there; Investor Status/Touchpoint
// moved in alongside them, same pattern, not a separate investor-id-scoped
// service — see that file for the actual GET/PUT calls). Notes & Files
// reuse the exact same actions/selectors ContactProfile.js (S6, homeowner)
// already wired up. Investor Status/Touchpoint are net-new here:
// investor-service exposes the two enum lists (GET .../investor-statuses,
// .../touchpoints), each optionally taking `?contactId=` to also flag this
// contact's currently saved row (`selected: true`) in the same response —
// there's no separate preload call any more (the old GET .../workflow
// endpoint is gone). Falls back to the first option — "New" / "Not Touched"
// — when nothing's selected yet, or the fetch itself fails; see constants.js.
//
// All four are keyed on the contact's own id (`investor.contactId`), not the
// BatchData investor id (`investor.id`) the rest of this modal uses — only
// available once the investor has actually been materialized into a contact
// (Contacts/InvestorProfileRoute.js sets it; Search's "View Details" never
// has one, since an investor there may not be saved to any list yet).
// Without it, these four sections render but stay inert.
const InvestorProfile = ({
  investor: searchResultInvestor,
  closePopup,
  onClose,
  // Optional (ATAI-467): Search results pass this so a contact edit made here
  // shows on the result card without a page refresh.
  onContactsUpdated,
}) => {
  const dispatch = useDispatch();
  const {
    profile,
    profileLoading,
    acquisitionProperties,
    dispositionProperties,
    propertiesTotal,
    propertiesLoading,
    refetchProfile,
  } = useInvestorProfileData(searchResultInvestor, (refetched) => {
    // Never blank the card's contacts if the re-read came back without any.
    if (Array.isArray(refetched?.contacts)) {
      onContactsUpdated?.(searchResultInvestor?.id, refetched.contacts);
      // ATAI-554: through the store too, so the map card (which opens this
      // Profile without onContactsUpdated) and the results card both pick the
      // edit up, whichever of them opened it.
      if (searchResultInvestor?.id) {
        dispatch(setInvestorContacts(searchResultInvestor.id, refetched.contacts));
      }
    }
  });

  // Real profile fields win over the search-result object once loaded;
  // anything the profile snapshot doesn't carry (status/touchpoint/notes —
  // separate contact-scoped calls, see below) keeps coming from the search
  // result.
  const investor = useMemo(
    () => (profile ? { ...searchResultInvestor, ...profile } : searchResultInvestor),
    [searchResultInvestor, profile]
  );

  // ATAI-186: `investor.contactId` is only ever set when opened from the
  // Contacts Investor List's thin {id, contactId} object — Search's "View
  // Details" never has one, even for an investor already genuinely saved.
  // A prior version fell back to `investor.savedGroups[0].contactId` from
  // the passive `profile` merge above, but that doesn't reliably resolve in
  // time here (unlike RemoveFromInvestorListModal, which works because
  // useSaveInvestor.js does its own eager, independent fetch at click time
  // rather than trusting this background merge) — so this does the same:
  // its own direct call to the lightweight saved-groups endpoint, decoupled
  // from useInvestorProfileData's fetch. One investor materializes to
  // exactly one contact row shared across every list it's saved to (S7
  // dedup by BatchData investor id), so any entry's contactId is the same
  // value.
  // The card already knows whether this investor is saved at all — its own
  // savedGroups came from the same S2 search response (groupId/groupName
  // present even where contactId isn't, e.g. this local-dev environment's
  // search route). An investor that isn't saved to anything will never have
  // a contactId to find, so there's nothing to fetch and nothing to wait on
  // — only a genuinely-saved investor needs the round trip below.
  const cardKnownSaved =
    Array.isArray(searchResultInvestor?.savedGroups) && searchResultInvestor.savedGroups.length > 0;

  const [savedGroupsContactId, setSavedGroupsContactId] = useState(null);
  // Skips the fetch (and the loading spinner) entirely when the Contacts
  // Investor List already handed this modal a real contactId up front, or
  // the card already told us this investor isn't saved to anything.
  const [resolvingSavedGroups, setResolvingSavedGroups] = useState(
    cardKnownSaved && !searchResultInvestor?.contactId
  );
  const searchResultInvestorId = searchResultInvestor?.id;

  useEffect(() => {
    if (!searchResultInvestorId || searchResultInvestor?.contactId || !cardKnownSaved) {
      setResolvingSavedGroups(false);
      return undefined;
    }
    let cancelled = false;
    setResolvingSavedGroups(true);

    fetchInvestorSavedGroups(searchResultInvestorId)
      .then(({ data }) => {
        if (cancelled) return;
        setSavedGroupsContactId(data?.find((group) => group.contactId != null)?.contactId ?? null);
      })
      .catch((error) => {
        console.warn('Could not load saved-group contact id:', error);
      })
      .finally(() => {
        if (!cancelled) setResolvingSavedGroups(false);
      });

    return () => {
      cancelled = true;
    };
  }, [searchResultInvestorId, searchResultInvestor?.contactId, cardKnownSaved]);

  const contactId = investor.contactId ?? savedGroupsContactId;

  // Default to the first option ("New" / "Not Touched") rather than a blank
  // "Select" placeholder -- product decision. The fetch effect below
  // overwrites this with a real saved value once it resolves, if there is
  // one.
  const [investorStatus, setInvestorStatus] = useState(
    InvestorStatusOptions[0]?.value ?? DefaultInvestorStatus
  );
  const [touchpoint, setTouchpoint] = useState(TouchpointOptions[0]?.value ?? DefaultTouchpoint);
  const [investorStatusOptions, setInvestorStatusOptions] = useState(InvestorStatusOptions);
  const [touchpointOptions, setTouchpointOptions] = useState(TouchpointOptions);
  // Separate flags, not one shared boolean — Investor Status and Touchpoint
  // save independently, so one field's in-flight PUT must not block a click
  // on the other.
  const [investorStatusSaving, setInvestorStatusSaving] = useState(false);
  const [touchpointSaving, setTouchpointSaving] = useState(false);
  // Starts true so the hardcoded defaults never paint before the saved values arrive (ATAI-387).
  const [workflowLoading, setWorkflowLoading] = useState(true);

  // One call per field gets both the option list AND (when contactId is
  // known) this contact's currently saved selection -- no more separate
  // preload call. Re-keyed on contactId since the `selected` flag is
  // contact-specific; Search's "View Details" has no contactId yet (the
  // section is hidden entirely in that case, further down), so this still
  // fetches the plain option list for when the modal later gets one.
  useEffect(() => {
    let cancelled = false;
    setWorkflowLoading(true);

    const statusesRequest = fetchInvestorStatuses(contactId)
      .then(({ data }) => {
        if (cancelled || !data?.length) return;
        setInvestorStatusOptions(toDropdownOptions(data));
        setInvestorStatus(selectedValue(data) ?? data[0]?.id ?? DefaultInvestorStatus);
      })
      .catch((error) => {
        console.warn('Could not load Investor Status options:', error);
        if (!cancelled) setInvestorStatus(investorStatusOptions[0]?.value ?? DefaultInvestorStatus);
      });

    const touchpointsRequest = fetchTouchpoints(contactId)
      .then(({ data }) => {
        if (cancelled || !data?.length) return;
        setTouchpointOptions(toDropdownOptions(data));
        setTouchpoint(selectedValue(data) ?? data[0]?.id ?? DefaultTouchpoint);
      })
      .catch((error) => {
        console.warn('Could not load Touchpoint options:', error);
        if (!cancelled) setTouchpoint(touchpointOptions[0]?.value ?? DefaultTouchpoint);
      });

    // Both chains already catch their own errors, so Promise.all always resolves.
    Promise.all([statusesRequest, touchpointsRequest]).then(() => {
      if (!cancelled) setWorkflowLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // Fallback options (investorStatusOptions[0] etc.) are read only inside
    // the catch branches, as a last resort if even the static defaults
    // somehow aren't there yet -- not a dependency this effect should
    // re-run for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const handleInvestorStatusChange = (value) => {
    if (!contactId || workflowLoading || investorStatusSaving) return;
    const previous = investorStatus;
    setInvestorStatus(value);
    setInvestorStatusSaving(true);

    putInvestorStatus(contactId, value)
      .catch((error) => {
        console.warn('Could not save Investor Status:', error);
        setInvestorStatus(previous);
      })
      .finally(() => setInvestorStatusSaving(false));
  };

  const handleTouchpointChange = (value) => {
    if (!contactId || workflowLoading || touchpointSaving) return;
    const previous = touchpoint;
    setTouchpoint(value);
    setTouchpointSaving(true);

    putTouchpoint(contactId, value)
      .catch((error) => {
        console.warn('Could not save Touchpoint:', error);
        setTouchpoint(previous);
      })
      .finally(() => setTouchpointSaving(false));
  };

  const extrasContactId = useSelector(selectContactExtrasContactId);
  const savedNote = useSelector(selectContactNote);
  const notesLoaded = useSelector(selectContactNoteLoaded);
  const noteSaving = useSelector(selectContactNoteSaving);
  const documents = useSelector(selectContactDocuments);
  const uploading = useSelector(selectContactDocumentUploading);
  const deletingDocument = useSelector(selectContactDocumentDeleting);
  const [notesValue, setNotesValue] = useState(savedNote || '');

  // Same fetch-on-mount / reset-on-unmount pattern as ContactProfile.js
  // (S6) — same global "current contact" slice, safe here since only one of
  // these two modals is ever open at a time.
  useEffect(() => {
    if (!contactId) {
      dispatch(resetContactExtras());
      return undefined;
    }

    dispatch(loadContactNotes(contactId, 'investors'));
    dispatch(loadContactDocuments(contactId));

    return () => dispatch(resetContactExtras());
  }, [contactId, dispatch]);

  // ATAI-388: re-seed the draft when the saved note OR its contact changes (so a
  // draft never carries over), during render so no frame paints a stale draft.
  const [notesSeed, setNotesSeed] = useState({ contactId: extrasContactId, note: savedNote });
  if (notesSeed.contactId !== extrasContactId || notesSeed.note !== savedNote) {
    setNotesSeed({ contactId: extrasContactId, note: savedNote });
    setNotesValue(savedNote || '');
  }

  const notesDirty = notesValue !== (savedNote || '');
  // ATAI-388: right after a switch the store still holds the previous contact.
  const notesBusy =
    Boolean(contactId) &&
    (!sameContactId(extrasContactId, contactId) ||
      !notesLoaded ||
      noteSaving ||
      uploading ||
      deletingDocument);

  // Property Details (PropertyTable's PropertyRow), the "Add to Investor
  // List" modal (IdentityHeader's bookmark), and ContactInfo's Edit Contact /
  // Skip Trace modals each open as their own stacked popup on top of this
  // one. SimpleModal's own outside-click listener has no idea any of them
  // exist — each is a separate DOM subtree, not a descendant of this modal's
  // own ref — so a click that closes one of them (it's "outside" that
  // popup's own content) reads as "outside this modal" too and closes both
  // at once. Suppressing this modal's outside-click/ESC handling while any
  // of them is open is the fix; all three flip this via the same callback
  // rather than this component knowing about any of them.
  const [suppressOutsideClick, setSuppressOutsideClick] = useState(false);
  // The same applies to the "Ready to Call" flow ContactInfo's phone
  // numbers open -- the host screen's <ClickToDial>, also outside this subtree.
  const isClickToDialOpen = useSelector(selectIsClickToDialOpen);

  // Contacts' Investor List (ATAI-180 follow-up) opens this modal outside the
  // route-driven Contact Detail flow, so it passes its own onClose to get
  // back to the contact list URL. Other callers (Search's "View Details")
  // don't pass one, so this is a no-op for them.
  const handleClose = () => {
    if (onClose) onClose();
    closePopup();
  };

  const handleNotesChange = (value) => setNotesValue(value);

  // Same guard as ContactProfile.js's own note save: the 4000-char cap is
  // measured on plain text so markup the editor emits isn't counted against
  // the user — investor-service stores the note as a @Lob and validates
  // nothing, so this cap is the only one there is.
  const handleSaveNotes = () => {
    if (!contactId || noteSaving) return;

    const plainTextLength = notesValue.replace(/<[^>]*>/g, '').trim().length;

    if (plainTextLength > 4000) {
      dispatch(
        InfoAlert.open({
          title: 'Invalid Note Length',
          infoText: 'Note length cannot exceed 4000 characters.',
        })
      );
      return;
    }

    dispatch(saveContactNote(contactId, notesValue, 'investors'));
  };

  // ButtonUpload (inside CommonNotes) hands back { value: { file } }, not a
  // raw change event — same shape ContactProfile.js's handleUploadFile reads.
  const handleUpload = ({ value: { file } = {} } = {}) => {
    if (file && contactId && !uploading) dispatch(uploadContactDocument(contactId, file));
  };

  // CommonNotes' remove button hands back the attachment's INDEX, not the
  // file (renderAttachments: `onRemoveAttachment(attachments.length - 1 - i)`),
  // so map it back to the row before hitting the endpoint.
  const handleRemoveAttachment = (index) => {
    const doc = (documents || [])[index];

    if (doc?.id && contactId && !deletingDocument) {
      dispatch(deleteContactDocument(contactId, doc.id));
    }
  };

  return (
    <SimpleModal
      isOpen
      uniqueId="investor-profile-modal"
      onCancel={handleClose}
      ignoreEscAndClickOutside={suppressOutsideClick || isClickToDialOpen}
      wrapperClassName={css.modalWrapper}
      modalClassName={css.panel}
      modalBodyClassName={css.body}
      modalFooterWrapperClassName={css.footerHidden}
      customFooter={<></>}
      customHeader={
        <div className={css.header}>
          <p className={css.header__title}>Investor Profile</p>
          <button
            type="button"
            className={css.header__close}
            onClick={handleClose}
            aria-label="Close"
          >
            {/* ATAI-372: the shared `iconClose` sprite (components/base/SVG)
                hardcodes fill="#99A0AA" on its own path -- a presentation
                attribute that wins over any `fill`/`color` set on this
                button from the outside, so `.header__closeIcon`'s color
                never actually reached it. Figma specs this one black, and
                the sprite is shared by several other grey close buttons
                app-wide, so it's inlined here instead of recoloring the
                shared symbol everywhere else uses it. */}
            <svg
              className={css.header__closeIcon}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M18.3002 5.70997C17.9102 5.31997 17.2802 5.31997 16.8902 5.70997L12.0002 10.59L7.11022 5.69997C6.72022 5.30997 6.09021 5.30997 5.70021 5.69997C5.31021 6.08997 5.31021 6.71997 5.70021 7.10997L10.5902 12L5.70021 16.89C5.31021 17.28 5.31021 17.91 5.70021 18.3C6.09021 18.69 6.72022 18.69 7.11022 18.3L12.0002 13.41L16.8902 18.3C17.2802 18.69 17.9102 18.69 18.3002 18.3C18.6902 17.91 18.6902 17.28 18.3002 16.89L13.4102 12L18.3002 7.10997C18.6802 6.72997 18.6802 6.08997 18.3002 5.70997Z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>
      }
      modalBody={
        <div className={css.profile}>
          <IdentityHeader
            investor={investor}
            loading={profileLoading}
            onInvestorListModalToggle={setSuppressOutsideClick}
          />

          {/* Investor Status / Touchpoint / Notes / Files all key on the
              contact record (see this file's header comment) — only
              available once the investor has been materialized into a
              contact (opened from the Contacts Investor List). Search's
              "View Details" never has one up front, so this shows a brief
              spinner while resolvingSavedGroups fetches it, then either the
              section (saved) or nothing (never saved) — never a jarring
              pop-in with no indication anything was loading. */}
          {resolvingSavedGroups ? (
            <div className={css.notesLoading}>
              <SVG icon="spinner" className={css.notesLoadingSpinner} />
            </div>
          ) : !contactId ? null : (
            <>
              {workflowLoading ? (
                <div className={css.notesLoading}>
                  <SVG icon="spinner" className={css.notesLoadingSpinner} />
                </div>
              ) : (
                <div className={css.workflow}>
                  <div className={css.workflow__field}>
                    <span className={css.workflow__label}>Investor Status</span>
                    <Dropdown
                      value={investorStatus}
                      options={investorStatusOptions}
                      onChange={handleInvestorStatusChange}
                      width="100%"
                      menuClassName={css.workflowMenu}
                      itemSelectedClassName={css.workflowItemSelected}
                    />
                  </div>
                  <div className={css.workflow__field}>
                    <span className={css.workflow__label}>Touchpoint</span>
                    <Dropdown
                      value={touchpoint}
                      options={touchpointOptions}
                      onChange={handleTouchpointChange}
                      width="100%"
                      menuClassName={css.workflowMenu}
                      itemSelectedClassName={css.workflowItemSelected}
                    />
                  </div>
                </div>
              )}

              {notesBusy ? (
                <div className={css.notesLoading}>
                  <SVG icon="spinner" className={css.notesLoadingSpinner} />
                </div>
              ) : (
                <CommonNotes
                  key={contactId}
                  className={css.notes}
                  value={notesValue}
                  onChange={handleNotesChange}
                  onSave={handleSaveNotes}
                  onUpload={handleUpload}
                  notesDirty={notesDirty}
                  // ATAI-372: no redundant "Notes" title on this card, and
                  // Figma's placeholder copy differs from the shared default.
                  hideTitle
                  placeholder="Add notes here"
                  attachments={documents || []}
                  // propstream-core exposes list + upload for contact
                  // documents but no download, so the chips stay inert on
                  // that action — same as ContactProfile.js (S6). Remove
                  // still works.
                  attachmentsDisabled
                  onRemoveAttachment={handleRemoveAttachment}
                  onDownloadAttachment={noop}
                  lastUpdated={formatContactSourceDate(investor.notesUpdatedAt)}
                />
              )}
            </>
          )}

          <ContactInfo
            investor={investor}
            contactId={contactId}
            onSkipTraced={refetchProfile}
            onContactSaved={refetchProfile}
            onModalToggle={setSuppressOutsideClick}
          />
          <div className={css.divider} />
          <MetricGrid investor={investor} />
          <div className={css.divider} />
          <History
            investor={investor}
            acquisitionProperties={acquisitionProperties}
            dispositionProperties={dispositionProperties}
          />
          <div className={css.divider} />
          <PropertyTable
            investor={investor}
            acquisitionProperties={acquisitionProperties}
            dispositionProperties={dispositionProperties}
            propertiesTotal={propertiesTotal}
            propertiesLoading={propertiesLoading}
            onPropertyDetailToggle={setSuppressOutsideClick}
          />
        </div>
      }
    />
  );
};

const registrationId = getPopupRegistration(InvestorProfile);
InvestorProfile.open = (props = {}) =>
  openPopup(registrationId, { ...props, priority: Priority.MEDIUM });
InvestorProfile.close = () => closePopup({ popup: registrationId });

export default InvestorProfile;
